import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    alpha,
    Autocomplete,
    Checkbox,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import {
    socialMediaService,
    PLATFORM_INFO,
} from "../../../api/services/socialMediaService";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { blogService } from "../../../api/services/blogService";

const campaignStatusConfig: Record<
    string,
    { bg: string; color: string; label: string }
> = {
    draft: { bg: "#F1F5F9", color: "#64748B", label: "Draft" },
    generating: { bg: "#EFF6FF", color: "#2563EB", label: "Generating" },
    ready: { bg: "#FFF7ED", color: "#EA580C", label: "Ready" },
    approved: { bg: "#F0FDF4", color: "#16A34A", label: "Approved" },
    posting: { bg: "#EFF6FF", color: "#2563EB", label: "Posting" },
    completed: { bg: "#F0FDF4", color: "#16A34A", label: "Completed" },
    partial: { bg: "#FFFBEB", color: "#D97706", label: "Partial" },
    failed: { bg: "#FEF2F2", color: "#DC2626", label: "Failed" },
};

const PLATFORM_OPTIONS = Object.entries(PLATFORM_INFO).map(([key, val]) => ({
    key,
    label: `${val.icon} ${val.name}`,
}));

const SocialCampaigns = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [createOpen, setCreateOpen] = useState(false);
    const [createMode, setCreateMode] = useState<"blog" | "prompt">("blog");
    const [blogId, setBlogId] = useState<number | "">("");
    const [promptTitle, setPromptTitle] = useState("");
    const [promptText, setPromptText] = useState("");
    const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
    const [autoApprove, setAutoApprove] = useState(false);

    const { data, isLoading } = useQuery(
        ["social-campaigns", { status: statusFilter || undefined, limit, offset: page * limit }],
        () =>
            socialMediaService.listCampaigns({
                status: statusFilter || undefined,
                limit,
                offset: page * limit,
            }),
        { keepPreviousData: true }
    );

    const { data: blogsData } = useQuery(
        ["blogs-for-social", { limit: 50, status: "PUBLISHED" }],
        () => blogService.getBlogs({ limit: 50, status: "PUBLISHED" as any }),
        { enabled: createOpen && createMode === "blog" }
    );

    const createFromBlogMutation = useMutation(
        (data: any) => socialMediaService.createFromBlog(data),
        {
            onSuccess: (res) => {
                toast.success("Campaign created from blog!");
                queryClient.invalidateQueries("social-campaigns");
                setCreateOpen(false);
                navigate(`/social-media/campaigns/${res.data?.id}`);
            },
            onError: () => {
                toast.error("Failed to create campaign");
            },
        }
    );

    const createFromPromptMutation = useMutation(
        (data: any) => socialMediaService.createFromPrompt(data),
        {
            onSuccess: (res) => {
                toast.success("Campaign created from prompt!");
                queryClient.invalidateQueries("social-campaigns");
                setCreateOpen(false);
                navigate(`/social-media/campaigns/${res.data?.id}`);
            },
            onError: () => {
                toast.error("Failed to create campaign");
            },
        }
    );

    const deleteMutation = useMutation(
        (id: number) => socialMediaService.deleteCampaign(id),
        {
            onSuccess: () => {
                toast.success("Campaign deleted");
                queryClient.invalidateQueries("social-campaigns");
            },
            onError: () => {
                toast.error("Failed to delete campaign");
            },
        }
    );

    const handleCreate = () => {
        if (createMode === "blog") {
            if (!blogId) {
                toast.error("Select a blog article");
                return;
            }
            createFromBlogMutation.mutate({
                articleId: blogId,
                targetPlatforms: selectedPlatforms.length ? selectedPlatforms : undefined,
                autoApprove,
            });
        } else {
            if (!promptTitle || !promptText) {
                toast.error("Title and prompt are required");
                return;
            }
            if (!selectedPlatforms.length) {
                toast.error("Select at least one platform");
                return;
            }
            createFromPromptMutation.mutate({
                title: promptTitle,
                prompt: promptText,
                targetPlatforms: selectedPlatforms,
                autoApprove,
            });
        }
    };

    const handleDelete = (id: number) => {
        if (window.confirm("Delete this campaign? All posts will be removed.")) {
            deleteMutation.mutate(id);
        }
    };

    if (isLoading && !data) return <Loader />;

    const campaigns = data?.data?.campaigns || [];
    const total = data?.data?.total || 0;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A" }}>
                        Campaigns
                    </Typography>
                    <Typography sx={{ color: "#64748B", mt: 0.5 }}>
                        AI-generated multi-platform content campaigns
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => setCreateOpen(true)}
                >
                    New Campaign
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(0);
                        }}
                        label="Status"
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(campaignStatusConfig).map(([key, val]) => (
                            <MenuItem key={key} value={key}>
                                {val.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Table */}
            <Card
                sx={{
                    borderRadius: "16px",
                    border: "1px solid rgba(226, 232, 240, 0.6)",
                    overflow: "hidden",
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Campaign</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Posts</TableCell>
                            <TableCell>Scheduled</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {campaigns.map((c: any) => {
                            const status =
                                campaignStatusConfig[c.status] || campaignStatusConfig.draft;
                            return (
                                <TableRow
                                    key={c.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => navigate(`/social-media/campaigns/${c.id}`)}
                                >
                                    <TableCell>
                                        <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
                                            {c.title}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={
                                                c.source_type === "blog" ? (
                                                    <ArticleRoundedIcon sx={{ fontSize: 14 }} />
                                                ) : (
                                                    <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
                                                )
                                            }
                                            label={c.source_type === "blog" ? "Blog" : "AI Prompt"}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: "11px" }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={status.label}
                                            size="small"
                                            sx={{
                                                backgroundColor: status.bg,
                                                color: status.color,
                                                fontWeight: 600,
                                                fontSize: "11px",
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>{c._count?.posts || 0}</TableCell>
                                    <TableCell>
                                        {c.scheduled_at
                                            ? new Date(c.scheduled_at).toLocaleString()
                                            : "-"}
                                    </TableCell>
                                    <TableCell>
                                        {new Date(c.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            display="flex"
                                            gap={0.5}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(c.id)}
                                            >
                                                <DeleteRoundedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!campaigns.length && (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                                    <AutoAwesomeRoundedIcon
                                        sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }}
                                    />
                                    <Typography sx={{ color: "#94A3B8", mb: 1 }}>
                                        No campaigns yet
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setCreateOpen(true)}
                                    >
                                        Create your first campaign
                                    </Button>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {total > 0 && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={limit}
                        onRowsPerPageChange={(e) => {
                            setLimit(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                )}
            </Card>

            {/* Create Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <AutoAwesomeRoundedIcon sx={{ color: "#7C3AED" }} />
                        Create AI Campaign
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                        {/* Mode Toggle */}
                        <Box display="flex" gap={1}>
                            <Button
                                variant={createMode === "blog" ? "contained" : "outlined"}
                                size="small"
                                startIcon={<ArticleRoundedIcon />}
                                onClick={() => setCreateMode("blog")}
                            >
                                From Blog
                            </Button>
                            <Button
                                variant={createMode === "prompt" ? "contained" : "outlined"}
                                size="small"
                                startIcon={<SendRoundedIcon />}
                                onClick={() => setCreateMode("prompt")}
                            >
                                From Prompt
                            </Button>
                        </Box>

                        {createMode === "blog" ? (
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Published Blog</InputLabel>
                                <Select
                                    value={blogId}
                                    onChange={(e) => setBlogId(e.target.value as number)}
                                    label="Select Published Blog"
                                >
                                    {blogsData?.data?.articles?.map((a: any) => (
                                        <MenuItem key={a.id} value={a.id}>
                                            {a.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <>
                                <TextField
                                    label="Campaign Title"
                                    value={promptTitle}
                                    onChange={(e) => setPromptTitle(e.target.value)}
                                    size="small"
                                    placeholder="e.g. World Speech Therapy Day"
                                />
                                <TextField
                                    label="AI Prompt"
                                    value={promptText}
                                    onChange={(e) => setPromptText(e.target.value)}
                                    size="small"
                                    multiline
                                    rows={4}
                                    placeholder="Describe the content you want generated for each platform..."
                                />
                            </>
                        )}

                        {/* Platform Selection */}
                        <Autocomplete
                            multiple
                            options={PLATFORM_OPTIONS}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option.label}
                            value={PLATFORM_OPTIONS.filter((o) =>
                                selectedPlatforms.includes(o.key)
                            )}
                            onChange={(_, values) =>
                                setSelectedPlatforms(values.map((v) => v.key))
                            }
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        checked={selected}
                                        sx={{ mr: 1 }}
                                    />
                                    {option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Target Platforms (leave empty for all connected)"
                                    size="small"
                                />
                            )}
                        />

                        <Box display="flex" alignItems="center" gap={1}>
                            <Checkbox
                                checked={autoApprove}
                                onChange={(e) => setAutoApprove(e.target.checked)}
                                size="small"
                            />
                            <Typography sx={{ fontSize: "13px" }}>
                                Auto-approve generated content (skip review)
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={
                            createFromBlogMutation.isLoading ||
                            createFromPromptMutation.isLoading
                        }
                        startIcon={<AutoAwesomeRoundedIcon />}
                    >
                        {createFromBlogMutation.isLoading ||
                            createFromPromptMutation.isLoading
                            ? "Generating..."
                            : "Generate Campaign"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SocialCampaigns;
